import React, { useEffect } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { useTranslations } from 'hooks/useTranslations';
import Grid from '@mui/material/Grid';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate, getDateDayName } from 'utils/date';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { StyledDayCard, StyledDayCardHeader, StyledDayCardItem } from './styles';
import { ETypeDay, ETypeTask } from './helpers';

const hours = Array.from({ length: 9 }, (_, i) => 8 + i);
const data = [
  {
    date: '2025-07-07T23:00:00+01:00[Europe/London]',
    type: ETypeDay.default,
    items: [
      {
        hour: 8,
        type: ETypeTask.applicationMeeting,
      },
      {
        hour: 9,
        type: ETypeTask.ownEvent,
      },
      {
        hour: 14,
        type: ETypeTask.applicationMeeting,
      },
    ],
  },
  {
    date: '2025-07-08T23:00:00+01:00[Europe/London]',
    type: ETypeDay.holiday,
    items: [],
  },
  {
    date: '2025-07-09T23:00:00+01:00[Europe/London]',
    type: ETypeDay.vacation,
    items: [],
  },
  {
    date: '2025-07-10T23:00:00+01:00[Europe/London]',
    type: ETypeDay.default,
    items: [
      {
        hour: 10,
        type: ETypeTask.applicationMeeting,
      },
      {
        hour: 15,
        type: ETypeTask.applicationMeeting,
      },
      {
        hour: 16,
        type: ETypeTask.applicationMeeting,
      },
    ],
  },
  {
    date: '2025-07-11T23:00:00+01:00[Europe/London]',
    type: ETypeDay.default,
    items: [
      {
        hour: 12,
        type: ETypeTask.ownEvent,
      },
      {
        hour: 14,
        type: ETypeTask.applicationMeeting,
      },
    ],
  },
];

const CalendarPage = () => {
  const { t } = useTranslations();
  const { getProperties, shortDateFormat } = useGlobalProperty();

  useEffect(() => {
    getProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Breadcrumbs items={[t('Calendar')]} />
      <StyledPageTitle variant="h4" sx={{ mb: 2 }}>
        {t('Calendar')}
      </StyledPageTitle>
      <Grid container columnSpacing="20px" sx={{ mb: 3 }}>
        <Grid container size={{ xs: 36 / 5 }} columnSpacing="20px">
          <Grid size={{ xs: 4 }}>
            <Button
              fullWidth
              label="previous week"
              icon={EIconTypes.left}
              variant={EButtonVariants.text}
              size={EButtonSizes.small}
            />
          </Grid>
          <Grid sx={{ textAlign: 'center', fontWeight: 600 }}>08/07/2025 – 14/07/2025</Grid>
          <Grid size={{ xs: 4 }}>
            <Button
              fullWidth
              label="next week"
              icon={EIconTypes.right}
              endIcon
              variant={EButtonVariants.text}
              size={EButtonSizes.small}
            />
          </Grid>
        </Grid>
        <Grid container size={{ xs: 24 / 5 }} columnSpacing="20px">
          <Grid size={{ xs: 6 }}>
            <Button
              fullWidth
              label="Settings"
              variant={EButtonVariants.outlined}
              size={EButtonSizes.small}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Button
              fullWidth
              label="add event"
              icon={EIconTypes.plus}
              variant={EButtonVariants.contained}
              size={EButtonSizes.small}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container columns={5} columnSpacing="20px">
        {data.map((day) => (
          <Grid size={{ xs: 1 }}>
            <StyledDayCard container size={{ xs: 12 }} type={day.type}>
              <StyledDayCardHeader type={day.type}>
                <Grid sx={{ fontWeight: 600 }}>{getDateDayName(day.date)}</Grid>
                <Grid>{formatDate(day.date, shortDateFormat)}</Grid>
              </StyledDayCardHeader>
              {day.type === ETypeDay.default ? (
                hours.map((hour) => {
                  const task = day.items.filter((item) => item.hour === hour)[0];
                  return (
                    <StyledDayCardItem container type={day.type} unavailable={!!task}>
                      <Grid sx={{ display: 'grid', lineHeight: 'normal' }}>
                        <span style={{ fontWeight: 600 }}>
                          {hour.toString().padStart(2, '0')}:00
                        </span>
                        <span>{task ? task.type : ETypeTask.freeSlot}</span>
                      </Grid>
                      <Grid sx={{ display: 'flex', gap: 0.5 }}>
                        {task && task.type === ETypeTask.ownEvent && (
                          <Grid>
                            <Button
                              variant={EButtonVariants.contained}
                              icon={EIconTypes.delete}
                              styleVariant="error"
                            />
                          </Grid>
                        )}
                        {task && (
                          <Grid>
                            <Button
                              variant={EButtonVariants.contained}
                              icon={EIconTypes.view}
                            />
                          </Grid>
                        )}
                        {!task && (
                          <Grid>
                            <Button
                              variant={EButtonVariants.outlined}
                              icon={EIconTypes.plus}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </StyledDayCardItem>
                  );
                })
              ) : (
                <Grid
                  sx={{
                    justifyItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignContent: 'flex-start',
                  }}
                >
                  <Grid sx={{ textAlign: 'center', height: '30px' }}>
                    {day.type === ETypeDay.vacation
                      ? 'Holiday Leave / Vacation'
                      : 'Bank Holiday'}
                  </Grid>
                  <Grid sx={{ textAlign: 'center', height: '30px' }}>
                    {day.type === ETypeDay.vacation && (
                      <Button
                        variant={EButtonVariants.contained}
                        icon={EIconTypes.delete}
                        styleVariant="error"
                      />
                    )}
                  </Grid>
                </Grid>
              )}
            </StyledDayCard>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default CalendarPage;
